import { useFetch } from '@reveni-io/reveni-ui'

import configurationServices from '@/configuration/services/services'

/**
 * @typedef {Object} ReturnPortalFeatures
 * @property {boolean} allows_refunds
 * @property {string} max_duration_for_refund
 * @property {boolean} shipping_included
 * @property {boolean} allows_exchanges
 * @property {string} max_duration_for_exchange
 * @property {boolean} allows_instant_exchanges
 * @property {string[]} allowed_exchange_methods
 * @property {string} issue_exchange_at_status
 * @property {boolean} instant_bank_transfer_allowed
 * @property {boolean} original_payment_method_allowed
 * @property {boolean} instant_original_payment_method_allowed
 * @property {boolean} store_credit_allowed
 * @property {boolean} instant_store_credit_allowed
 * @property {string[]} allowed_same_product_exchange_comparison_methods
 * @property {boolean} manage_restock
 * @property {boolean} instant_exchange_orders_automatic_creation
 * @property {boolean} custom_return_reasons
 * @property {boolean} exchange_orders_send_provider_notification
 * @property {boolean} instant_exchange_orders_send_provider_notification
 * @property {string} product_variant_exchange_recommendation_type
 * @property {string} product_variant_exchange_recommendation_value
 * @property {string} returns_policy_url
 * @property {string} exchange_error_resolution
 * @property {string} issue_refund_at_status
 * @property {boolean} show_rejected
 * @property {string} help_center_url
 * @property {string} return_reasons_sorting
 * @property {string} refund_reference_date
 * @property {string} exchange_reference_date
 * @property {boolean} sales_orders_data_activated
 * @property {boolean} gift_card_allowed
 * @property {boolean} allows_cancelling_gift_card
 * @property {boolean} allows_upsells
 * @property {boolean} shipping_skip_packing
 * @property {boolean} shipping_fulfillment
 * @property {boolean} exchange_orders_automatic_creation
 * @property {number} return_cancel_deadline_days
 * @property {string} return_reasons_filtering
 * @property {string} product_variant_exchange_taxonomy_type
 * @property {string[]} product_variant_exchange_available_options
 * @property {string} product_variant_exchange_taxonomy_display
 * @property {string[]} product_variant_exchange_taxonomy_ids
 * @property {(null|string)[]} available_issue_refund_status
 * @property {string[]} available_issue_exchange_status
 * @property {boolean} email_editor_enabled
 * @property {boolean} shipments_email_editor_enabled
 * @property {boolean} allows_instant_transactions
 * @property {boolean} recommendation_widget_enabled
 * @property {boolean} bracketing_widget_enabled
 * @property {boolean} smart_tracking_enabled
 * @property {boolean} allows_policy_rule_disable_instant
 * @property {boolean} shipments_ai_generate_email_enabled
 * @property {string} label_format
 * @property {'email'|'url'|'email_and_url'} contact_method
 * @property {boolean} display_carrier_tracking_page_action
 */

/**
 * @param {{returnPortalId: string, doInitialRequest?: boolean }} params
 * @returns {{
 *  returnPortalFeatures: ReturnPortalFeatures|undefined,
 *  refetchReturnPortalFeatures: () => Promise<ReturnPortalFeatures>,
 *  fetchingReturnPortalFeatures: boolean,
 *  editReturnPortalPreferences: (params: {returnPortalId: string, payload: Object}) => Promise<any>,
 *  editingReturnPortalPreferences: boolean,
 *  setReturnPortalFeatures: (updater: Function) => void,
 *  hasReturnPortalFeaturesChanges: boolean,
 *  resetReturnPortalFeatures: () => void
 * }}
 */

const useReturnPortalFeatures = ({ returnPortalId, doInitialRequest = true }) => {
  const {
    data: returnPortalFeatures,
    fetchData: refetchReturnPortalFeatures,
    loading: fetchingReturnPortalFeatures,
    setData: setReturnPortalFeatures,
    hasChanges: hasReturnPortalFeaturesChanges,
    resetData: resetReturnPortalFeatures,
  } = useFetch(() => configurationServices.getPortalPreferences({ returnPortalId }), {
    doInitialRequest,
  })

  const { fetchData: editReturnPortalPreferences, loading: editingReturnPortalPreferences } = useFetch(
    configurationServices.editPortalPreferences,
    { doInitialRequest: false }
  )

  return {
    returnPortalFeatures,
    refetchReturnPortalFeatures,
    fetchingReturnPortalFeatures,
    editReturnPortalPreferences,
    editingReturnPortalPreferences,
    setReturnPortalFeatures,
    hasReturnPortalFeaturesChanges,
    resetReturnPortalFeatures,
  }
}

export default useReturnPortalFeatures
